import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AppBar, // Not directly used as a top bar, but good for context if header was more complex
  Toolbar,
  Typography,
  Container,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
  Paper,
  Stack,
  createTheme,
  ThemeProvider,
  CssBaseline,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // For the checkmark in message
import { use } from 'react';
import { get } from '../../utility/fetchWeb';
import { get as gets } from '../../utility/fetch';

import SearchUser from './SearchUser';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns'; // Import date formatting library
import toast from 'react-hot-toast';

// Custom MUI theme
const theme = createTheme({
    palette: {
        primary: {
            main: '#28a745', // Green for online indicator and accents
        },
        secondary: {
            main: '#f8f9fa', // Light background for the overall page
        },
        background: {
            default: '#f8f9fa',
            paper: '#fff', // White background for the chat list Paper
        },
        text: {
            primary: '#333',
            secondary: '#6c757d',
        },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        h5: {
            fontWeight: 600,
            fontSize: '1.8rem',
            '@media (max-width:600px)': {
                fontSize: '1.5rem',
            },
        },
        subtitle1: { // For chat names
            fontWeight: 500,
            fontSize: '1.05rem',
        },
        body2: { // For last message and date
            fontSize: '0.9rem',
        },
        caption: {
            fontSize: '0.75rem',
        }
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: '16px', // Rounded corners for the main chat list card
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)', // Subtle shadow
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '25px', // Rounded search bar
                        backgroundColor: '#f5f5f5', // Light background
                        '& fieldset': {
                            borderColor: 'transparent', // No border by default
                        },
                        '&:hover fieldset': {
                            borderColor: '#e0e0e0', // Subtle border on hover
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#28a745', // Green border on focus
                        },
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: '#6c757d', // Default icon button color
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontWeight: 600, // Make names bolder
                    lineHeight: 1.2,
                },
                secondary: {
                    color: '#6c757d', // Grey for message and date
                    display: 'flex', // To align checkmark and text
                    alignItems: 'center',
                    gap: '4px',
                }
            }
        }
    },
});

function ChatList() {
  const [patients, setPatients] = React.useState([]);
  const [chats, setChats] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 20;
  const [totalPages, setTotalPages] = useState(1);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const observerRef = useRef(null);
  const listEndRef = useRef(null);
  const navigate = useNavigate();

  const userId = sessionStorage.getItem('userId');

   const fetchPresenceForContacts = async (contacts) => {
    try {
      const enriched = await Promise.all(
        contacts.map(async (c) => {
          try {
            // Choose fetch function based on userType: 1 => get, 2 => gets, default => get
            const fetchFn = c?.userType === 1 ? get : (c?.userType === 2 ? gets : get);
            const res = await fetchFn(`/UserPresence/status/${c.id}`);
            return { ...c, isOnline: res?.data?.status === 'online' };
          } catch {
            return { ...c, isOnline: false };
          }
        })
      );
      return enriched;
    } catch {
      return contacts.map(c => ({ ...c, isOnline: false }));
    }
  };

  const fetchChats = useCallback(
    async ({ page = 1, term = '', append = false } = {}) => {
      const isSearch = term.trim().length > 0;
      if (page === 1 && append === false) {
        if (isSearch) setSearching(true);
        else setLoadingInitial(true);
      } else {
        setLoadingMore(true);
      }

      try {
        let url = `/Posts/contacts/${userId}/2?pageNumber=${page}&pageSize=${pageSize}`;
        if (term) url += `&searchTerm=${encodeURIComponent(term)}`;

        const res = await get(url);
        const {
          contacts = [],
          totalContacts: total = 0,
          pageNumber: current = page,
          totalPages: tp = 1
        } = res || {};

        const contactsWithPresence = await fetchPresenceForContacts(contacts);

        setTotalContacts(total);
        setTotalPages(tp);
        setHasMore(current < tp);
        setPageNumber(current);

        setChats(prev =>
          append ? [...prev, ...contactsWithPresence] : contactsWithPresence
        );
      } catch (error) {
        console.error('Error fetching chats:', error);
        if (page === 1 && !append) {
          setChats([]);
          setTotalContacts(0);
          setHasMore(false);
        }
      } finally {
        setLoadingInitial(false);
        setLoadingMore(false);
        setSearching(false);
      }
    },
    [userId]
  );

  // Initial load
  useEffect(() => {
    fetchChats({ page: 1, term: searchTerm, append: false });
  }, [fetchChats]);

  // Infinite scroll observer
  useEffect(() => {
    if (!listEndRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      entries => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loadingMore && !loadingInitial && !searching) {
          fetchChats({ page: pageNumber + 1, term: searchTerm, append: true });
        }
      },
      { threshold: 1 }
    );
    observerRef.current.observe(listEndRef.current);
  }, [fetchChats, hasMore, loadingMore, loadingInitial, searching, pageNumber, searchTerm]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchChats({ page: 1, term: searchTerm, append: false });
  };

  const clearSearch = () => {
    if (searchTerm === '') return;
    setSearchTerm('');
    fetchChats({ page: 1, term: '', append: false });
  };

  const getAllpatients = async () => {
      try {
          let res = await get(`/patient`);
          console.log(res);
          setPatients(res?.data || []);
      } catch (error) {
          console.error("Error fetching patients:", error);
      }
  }

  useEffect(() => {
      // Simulate fetching chats
      fetchChats({ page: 1, term: '', append: false });
      // getAllpatients();
  }, []);

  const handleOpenChat = (chat) => {
    const uType = Number(chat?.userType);
    if (uType === 1 || uType === 2) {
      navigate(`/chat?rrt=${chat.id}&s=2&r=${uType}`);
    } else {
      toast.error('No chat found');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
        <Container maxWidth="md">
          {/* Header */}
            <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 1, ml: { xs: 0, sm: 0 } }}>
              Chats
            </Typography>

         

          {/* Search */}
          <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                variant="outlined"
                placeholder="Search chats..."
                fullWidth
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <IconButton type="submit" sx={{ border: '1px solid rgba(0,0,0,0.12)' }}>
                <SearchIcon />
              </IconButton>
              {/* <IconButton aria-label="filter">
                <FilterListIcon />
              </IconButton> */}
            </Stack>
          </Box>

          {/* Main List Area */}
          {loadingInitial ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: '16px',
                p: 6,
                textAlign: 'center',
                minHeight: 320,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 2
              }}
            >
              <CircularProgress sx={{ color: '#2e7d32' }} />
              <Typography variant="body1" fontWeight={500}>
                Loading chats...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Please wait while we fetch your conversations.
              </Typography>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden', position: 'relative' }}>
              {chats.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Chats Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? 'Try a different search term.' : 'Start a conversation by selecting a contact.'}
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {chats.map((chat, index) => (
                    <React.Fragment key={`${chat.id}-${index}`}>
                      <ListItem
                        button
                        sx={{
                          py: 1.5,
                          px: { xs: 2, sm: 3 },
                          cursor: 'pointer',
                          '&:active': { backgroundColor: 'rgba(0,0,0,0.05)' },
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' }
                        }}
                        onClick={() => handleOpenChat(chat)}
                      >
                        <ListItemAvatar>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            variant="dot"
                            sx={{
                              '& .MuiBadge-badge': {
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                border: '2px solid white',
                                backgroundColor: chat.isOnline ? '#2e7d32' : '#b0b0b0'
                              }
                            }}
                            aria-label={chat.isOnline ? 'Online' : 'Offline'}
                          >
                            <Avatar alt={chat.firstName} src={chat?.pictureUrl || ''} />
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" noWrap fontWeight={600}>
                              {chat.firstName} {chat.lastName}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              {chat.isRead && (
                                <CheckCircleIcon sx={{ fontSize: '0.9rem', color: '#2e7d32' }} />
                              )}
                              <Typography variant="body2" noWrap>
                                {chat.lastMessage}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {chat.lastMessageSentAt
                                  ? format(new Date(chat.lastMessageSentAt), 'MMM d, h:mm a')
                                  : ''}
                              </Typography>
                            </Box>
                          }
                          sx={{ ml: 1 }}
                        />
                        <ListItemIcon sx={{ minWidth: 40, ml: 1 }}>
                          <Badge
                            badgeContent={chat.unreadMessagesCount}
                            color="error"
                            invisible={chat.unreadMessagesCount === 0}
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.7rem',
                                height: 18,
                                minWidth: 18
                              }
                            }}
                          >
                            <ChevronRightIcon color="action" />
                          </Badge>
                        </ListItemIcon>
                      </ListItem>
                      {index < chats.length - 1 && <Divider component="li" sx={{ ml: 8, mr: 2 }} />}
                    </React.Fragment>
                  ))}
                  {/* Sentinel for infinite scroll */}
                  <Box ref={listEndRef} sx={{ height: 1 }} />
                </List>
              )}

              {/* Loading more indicator */}
              {loadingMore && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    py: 2
                  }}
                >
                  <CircularProgress size={22} sx={{ color: '#2e7d32' }} />
                  <Typography variant="caption" color="text.secondary">
                    Loading more chats...
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default ChatList;